import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';
import { actionItemQueryKeys } from './actionItemQueryKeys';

export function useActionItemSummaryQuery({
	impersonationContext,
	enabled = true,
}: {
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
} = {}) {
	const userSession = useUserSession();
	const now = new Date();
	const dayKey = format(now, 'yyyy-MM-dd');
	return useQuery<Api.IActionItemSummary>({
		enabled,
		queryKey: actionItemQueryKeys.summary(impersonationContext?.account?.id, dayKey),
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.IActionItemSummary>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						todayStart: startOfDay(now).toISOString(),
						todayEnd: endOfDay(now).toISOString(),
					},
					urlPath: 'actionItem/summary',
				}),
				'GET'
			),
		staleTime: 60 * 1000,
		refetchInterval: 5 * 60 * 1000,
		refetchOnWindowFocus: true,
		// A passive badge should not toast on every failed refresh, so this logs without a toast.
		meta: { errorLogging: { category: 'ActionItem', action: 'ActionItemSummaryQuery-Error' } },
	});
}

export function invalidateActionItemSummary() {
	reactQueryClient.invalidateQueries({ queryKey: actionItemQueryKeys.summaryPrefix });
}
